import { useContext } from 'react';
import _ from 'underscore';
import { usePromise, usePromiseCallback } from '@bingads-webui-react/async-utils';
import { CcJsDataContext } from '../js-data-context';

/**
 * Use when you need to manually access jsData from context.
 * Example: When your component renders a legacy backbone
 * grid which takes 'jsData' as a prop.
 * @param {object} options - Options object
 * @param {boolean} [options.isStrictMode=true] - Controls if the hook should throw
 * an error when jsData is not available in context.
 * @returns {object} the jsData context object
 */
export const useJsDataContext = ({ isStrictMode = true } = {}) => {
  const jsData = useContext(CcJsDataContext);

  if (isStrictMode && !jsData) {
    throw new Error('Please make sure to initialize the jsDataContext in your app before consuming it.');
  }

  return jsData;
};

/**
 * Use when you need to declaratively access data from jsData.
 * This is an abstraction of the pattern where you:
 * 1. Defined state to capture the response of an API call
 * 2. Keep track of the request with a 'loading' property
 * 3. Cleanup the request token when needed to prevent memory leaks.
 * @param {function} getPromise - Function that takes jsData as a prop
 * and returns a promise that resolves to the api request response.
 * example: (jsData) => jsData.Account.findAll({ customerId })
 * @param {*} dependencies - Controls the execution as hook. Uses useEffect
 * internally.
 * @param {object} options - Options object
 * @param {boolean} options.isStrictMode - Controls if the hook should throw
 * an error when jsData is not available in context.
 * @returns {object} Object containing the following props:
 * - data: contains the promise resolved value
 * - loading: keeps track of the promise state (true when pending, false otherwise)
 * - error: contains the rejected value in case the promise threw one
 */
export const useJsData = (getPromise, dependencies = [], options) => {
  const jsData = useJsDataContext(options);

  const loaderDependencies = [
    jsData,
    ...dependencies,
  ];

  return usePromise(
    () => getPromise(jsData),
    loaderDependencies
  );
};

/**
 * Use when you need to declaratively access data from jsData,
 * but there's a 'manual' trigger that controls when the data is required.
 * Can be controlled by user interaction for example.
 * @param {function} getPromise - Function that takes jsData as a prop
 * and returns a promise that resolves to the api request response.
 * example: (jsData) => jsData.Account.findAll({ customerId })
 * @param {Array} dependencies Controls the memoization of the 'execute' function.
 * The identity of 'execute' needs to be stable so it can be used as part of
 * a effect or callback dependency.
 * @param {object} options - Options object
 * @param {boolean} options.isStrictMode - Controls if the hook should throw
 * an error when jsData is not available in context.
 * @returns {object} Object containing the following props:
 * - data: contains the promise resolved value
 * - loading: keeps track of the promise state (true when pending, false otherwise)
 * - error: contains the rejected value in case the promise threw one
 * - execute: function that needs to be called when we need to request the data.
 */
export const useJsDataCallback = (getPromise, dependencies, options) => {
  const jsData = useJsDataContext(options);

  const loaderDependencies = _.isArray(dependencies)
    ? [jsData, ...dependencies]
    : undefined;

  return usePromiseCallback(
    () => getPromise(jsData),
    loaderDependencies
  );
};
